import { useState, useEffect, useCallback } from "react";
import { createContext, useContext, useReducer } from "react";

import * as DialogueEngine from "./DialogueEngine.js";
import { compress } from "compress-json";

import Wait from "./Wait.js";
import ConnectionLost from "./ConnectionLost.js";
import InternalError from "./InternalError.js";
import AlertList from "./AlertList.js";

const VERBOSE = false;
const REFRESH_TIME = 3000;

let refreshInterval;
let datetime = null;

const LOCAL = window.location.hostname === "localhost";
export const PHP_PATH = LOCAL ? "http://localhost/2cpo/public/php/" : "./php/";
export const API_PATH = LOCAL ? "http://localhost:3000/" : "https://legamelab.com/2cpo/";

export const GAME_ID = "ada";


const dataInit = {
    gameId: GAME_ID,
    session: {
	main: false,
	password: null,
	currentGameId: null,
	currentSessionId: null,
	sessions: {},
	plays: {},
	allPlays: {}
    },
    state: {
	showModals: {
	    title: false,
	    end: false,
	    map: false,
	    phone: false,
	    company: false,
	    report: false,
	    analysis: false,
	    result: false,
	    application: false,
	    dialogue: false,
	    information: false,
	    items: false,
	},
	currentGameId: null,
	currentPlayId: null,
	currentThemeId: null,
	currentPlaceId: null,
	currentSolutionId: null,
	currentDialogue: null,
	currentInformationId: null,
	time: null,
	timeOut: false,
	money: 200000,
	score: 0,
	errors: 0,
	vars: {},
	places: {},
	chars: {},
	badges: {},
	variables: {},
	themes: {},
	solutions: {},
	documents: {},
	items: {},
	reports: {},
	charQuests: {},
	choiceQuests: {},
    },
    alerts: {},
    content: {}
};

// TODO : do not add content in data context ?


const setLocal = (field, value) => {
    localStorage.setItem(field, JSON.stringify(value));
};

const getLocal = (field) => {
    try { return JSON.parse(localStorage.getItem(field)); }
    catch (error) { return null; }
};

async function setGlobal (state)
{
    if (state.currentPlayId !== undefined)
    {
	const sentState = { badges: state.badges, items: state.items, errors: state.errors, score: state.score };
	
	const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ playId: state.currentPlayId, playState: JSON.stringify(compress(state)) }),
	};

	try { await fetch(PHP_PATH + "savePlay.php", requestOptions); }
	catch (error) {}
    }
}


function dataReducer (data, action)
{
    var newAlerts = { ...data.alerts };
    var newSession = { ...data.session };
    var newState = { ...data.state };
    var newContent = { ...data.content };

    if (action.alert !== undefined)
    {
	const alertID = Object.keys(newAlerts).length;
	newAlerts[alertID] = { alertID, ...action.alert };
    }

    // TODO : reduce !
    if (action.session !== undefined)
    {
	Object.keys(action.session).forEach((sessionKey) => {
	    if (typeof newSession[sessionKey] === "object" && newSession[sessionKey] !== null && action.session[sessionKey] !== null)
	    {
		Object.entries(action.session[sessionKey]).forEach(([key, value]) => {
		    if (value === null) delete newSession[sessionKey][key];
		    else newSession[sessionKey][key] = value;
		});
	    }
	    
	    else { newSession[sessionKey] = action.session[sessionKey]; }
	});
    }
    
    if (action.state !== undefined)
    {
	Object.keys(action.state).forEach((stateKey) => {
	    if (stateKey === "currentDialogue") newState["currentDialogue"] = action.state["currentDialogue"]; // TODO : make more systematic
	    else if (typeof newState[stateKey] === "object" && newState[stateKey] !== null && action.state[stateKey] !== null)
	    {
		Object.entries(action.state[stateKey]).forEach(([key, value]) => {
		    if (value === null) delete newState[stateKey][key];
		    else newState[stateKey][key] = value;
		});
	    }
	    
	    else { newState[stateKey] = action.state[stateKey]; }
	});
    }
    
    if (action.content !== undefined)
    {
	Object.keys(action.content).forEach((contentKey) => {
	    if (typeof newContent[contentKey] === "object" && newContent[contentKey] !== null && action.content[contentKey] !== null)
	    {
		Object.entries(action.content[contentKey]).forEach(([key, value]) => {
		    if (value === null) delete newContent[contentKey][key];
		    else newContent[contentKey][key] = value;
		});
	    }
	    
	    else { newContent[contentKey] = action.content[contentKey]; }
	});
    }

    if (newState.currentPlayId !== null) setLocal("playState", newState);

    if (action.save) setGlobal(newState);
    
    return { gameId: data.gameId, session: newSession, state: newState, alerts: newAlerts, content: newContent };
}

const DataContext = createContext(null);

const DispatchContext = createContext(null);

export function DataProvider ({ children })
{
    const [loading, setLoading] = useState(false);
    const [connectionLost, setConnectionLost] = useState(null);
    const [internalError, setInternalError] = useState(null);
    const [data, dispatch] = useReducer(dataReducer, dataInit);

    const asyncDispatch = useCallback((action) => {
	if (VERBOSE) console.log("START DISPATCH " + action.type);
	switch (action.type)
	{
	    case "alert" :
	    dispatch({ alert: { ...action.data } });
	    break;

	    case "alertTime" :
	    dispatch({
		state: {
		    themes: { [data.state.currentThemeId]: { ...data.state.themes[data.state.currentThemeId], alertTime: action.time } },
		    timeOut: action.time === 0
		},
		alert: { ...action.data }
	    });
	    break;

	    case "dispatch" :
	    dispatch({ state: { ...action.data } });
	    break;

	    case "openModal" :
	    dispatch({ state: { showModals: { [action.modal]: true } } });
	    break;
	    
	    case "closeModal" :
	    dispatch({ state: { showModals: { [action.modal]: false } } });
	    break;
	    
	    case "openEnd" :
	    dispatch({ state: { showModals: { end: true }, currentThemeId: action.themeId } });
	    break;
	    
	    case "closeEnd" :
	    dispatch({ state: { showModals: { end: false }, currentThemeId: null } });
	    break;
	    
	    case "openInformation" :
	    dispatch({ state: { showModals: { information: true }, currentInformationId: action.informationId } });
	    break;	    

	    case "closeInformation" :
	    dispatch({ state: { showModals: { information: false }, currentInformationId: null } });
	    break;	    

	    case "openReport" :
	    dispatch({ state: { showModals: { report: true } } });
	    break;	    

	    case "closeReport" :
	    dispatch({ state: { showModals: { report: false } } });
	    break;	    

	    case "openAnalysis" :
	    dispatch({ state: { showModals: { analysis: true }, currentSolutionId: action.solutionId } });
	    break;	    

	    case "closeAnalysis" :
	    dispatch({ state: { showModals: { analysis: false }, currentSolutionId: null } });
	    break;	    

	    case "openResult" :
	    dispatch({ state: { showModals: { result: true }, currentSolutionId: action.solutionId } });
	    break;	    

	    case "closeResult" :
	    dispatch({ state: { showModals: { result: false }, currentSolutionId: null } });
	    break;	    

	    case "openApplication" :
	    dispatch({ state: { showModals: { application: true }, currentSolutionId: action.solutionId } });
	    break;	    

	    case "closeApplication" :
	    dispatch({ state: { showModals: { application: false }, currentSolutionId: null } });
	    break;

	    case "setVar" :
	    dispatch({ state: { vars: { [action.var]: action.value } } });
	    break;	    

	    case "setVars" :
	    {
		let newVars = {};
		const nb = Math.max(action.vars.length, action.values.length);
		for (let i = 0; i < nb; i++) newVars[action.vars[i]] = action.values[i];
		dispatch({ state: { vars: newVars } });
	    }
	    break;

	    case "adminLogin" :
	    {
		const callback = (receivedJSON) => {
		    if (receivedJSON.status === "passwordOK") { dispatch({ session: { password: action.password } }); }
		    else if (receivedJSON.status === "passwordMain") { dispatch({ session: { password: action.password, main: true } }); }
		    else if (receivedJSON.status === "passwordKO") asyncDispatch({ type: "alert", data: { variant: "danger", title: "Mot de passe non reconnu" }});
		    else asyncDispatch({ type: "alert", data: { variant: "danger", title: "Erreur interne" }});
		};
		
		api("adminLogin.php", { password: action.password }, true, callback);
	    }
	    break;

	    case "loadStats" :
	    {
		const callback = (receivedJSON) => {
		    if (receivedJSON.status === "passwordMain") { dispatch({ session: { allPlays: receivedJSON.plays } }); }
		    else asyncDispatch({ type: "alert", data: { variant: "danger", title: "Erreur interne" }});
		};
		
		api("loadStats.php", { password: action.password, gameId: data.gameId }, true, callback);
	    }
	    break;

	    case "showPlay" :
	    {
		const callback = (receivedJSON) => {
		    if (receivedJSON.status === "showOK") dispatch({ session: { plays: receivedJSON.plays } });
		    else asyncDispatch({ type: "alert", data: { variant: "danger", title: "Erreur interne" }});
		};

		api("showPlay.php", { code: action.code, password: action.password, show: action.show, playId: action.playId }, true, callback);
	    }
	    break;

	    case "stopSession" :
	    {
		const callback = (receivedJSON) => {
		    if (receivedJSON.status === "stopOK")
		    {
			clearInterval(refreshInterval);
			dispatch({ session: { sessions: receivedJSON.sessions } });
		    }
		    else asyncDispatch({ type: "alert", data: { variant: "danger", title: "Erreur interne" }});
		};
		
		api("stopSession.php", { code: action.code, password: action.password, endTime: action.endTime }, true, callback);
	    }
	    
	    case "newSession" :
	    case "loadSession" :
	    {
		const callback = (receivedJSON) => {
		    if ((action.type === "newSession" && receivedJSON.status === "passwordOK") || (action.type === "loadSession" && receivedJSON.status === "codeOK"))
		    {
			setLoading(true);
			
			let plays = {};
			if (action.type === "loadSession") plays = receivedJSON.plays;
			const session = { currentGameId: data.gameId, currentSessionId: receivedJSON.sessionId, sessions: receivedJSON.sessions, plays };

			const content = {};

			const files = [];
			const promises = [];

			files.push({ type: "params", url: data.gameId + "/params.json" });
			files.push({ type: "chars", url: data.gameId + "/chars.json" });
			files.push({ type: "places", url: data.gameId + "/places.json" });
			files.push({ type: "themes", url: data.gameId + "/themes.json" });

			files.forEach((file) => {
			    promises.push(
				fetch(file.url)
				    .then((response) => { return response.text(); })
				    .then((text) => { content[file.type] = JSON.parse(text); })
			    );
			});

			Promise.all(promises)
			       .then(() => {

				   const files = [];
				   const promises = [];
				   const themeIds = Object.keys(content.themes);

				   let results = {};
				   if (content.params.solutionGameplay)
				   {
				       results = { ...results, solutions: {}, documents: {} };

				       files.push({ type: "variables", url: data.gameId + "/variables.json" });

				       themeIds.forEach((themeId) => {
					   files.push({ type: "solutions", themeId, url: data.gameId + "/themes/" + themeId + "_solutions.json" });
					   files.push({ type: "documents", themeId, url: data.gameId + "/themes/" + themeId + "_documents.json" });
				       });
				   }
				   
				   if (content.params.itemGameplay)
				   {
				       results = { ...results, items: {} };
				       
				       themeIds.forEach((themeId) => {
					   files.push({ type: "items", themeId, url: data.gameId + "/themes/" + themeId + "_items.json" });
				       });
				   }

				   files.forEach((file) => {
				       promises.push(
					   fetch(file.url)
					       .then((response) => { return response.text(); })
					       .then((text) => {
						   if (file.type === "dialogue") DialogueEngine.start(JSON.parse(text).content);
						   else {
						       if (file.themeId === undefined) content[file.type] = JSON.parse(text);
						       else results[file.type][file.themeId] = JSON.parse(text);
						   }
					       })
				       );
				   });

				   Promise.all(promises)
					  .then(() => {

					      if (content.params.solutionGameplay)
					      {
						  content.solutions = {};
						  Object.values(results.solutions).forEach(solutions => Object.values(solutions).forEach(solution => content.solutions[solution.id] = solution));

						  content.documents = {};
						  Object.values(results.documents).forEach(documents => Object.values(documents).forEach(document => content.documents[document.id] = document));
					      }
					      
					      if (content.params.itemGameplay)
					      {
						  content.items = {};
						  Object.values(results.items).forEach(items => Object.values(items).forEach(item => content.items[item.id] = item));
					      }

					      dispatch({ content, session });
					      setLoading(false);

					      if (session.sessions[session.currentSessionId].endTime === null)
					      {
						  clearInterval(refreshInterval);
						  const code = session.sessions[session.currentSessionId].code;
						  refreshInterval = setInterval(() => {
						      asyncDispatch({ type: "updateSession", code, password: action.password });
						  }, REFRESH_TIME);
					      }
					  });
			       });
		    }
		    
		    else if (action.type === "newSession" && receivedJSON.status === "passwordKO") asyncDispatch({ type: "alert", data: { variant: "danger", title: "Code d'accès inconnu" }});

		    else if (action.type === "loadSession" && receivedJSON.status === "codeKO") asyncDispatch({ type: "alert", data: { variant: "danger", title: "Code d'accès inconnu" }});

		    else asyncDispatch({ type: "alert", data: { variant: "danger", title: "Erreur interne" }});
		};

		if (action.type === "newSession") api("newSession.php", { password: action.password, name: action.name, gameId: data.gameId }, true, callback);
		else if (action.type === "loadSession") api("loadSession.php", { code: action.code, password: action.password, gameId: data.gameId }, true, callback);
	    }
	    break;

	    case "closeSession" :
	    clearInterval(refreshInterval);
	    window.location.reload();
	    //dispatch({ session: { currentGameId: null, currentSessionId: null, session: null } });
	    break;

	    case "updateSession" :
	    const callback = (receivedJSON) => {
		if (receivedJSON.plays !== undefined) dispatch({ session: { plays: receivedJSON.plays } });
		datetime = receivedJSON.datetime;
	    };
	    api("updateSession.php", { sessionId: action.sessionId, code: action.code, password: action.password, datetime }, false, callback);
	    break;

	    case "newPlay" :
	    case "loadPlay" :
	    {
		if (action.type === "newPlay")
		{
		    setLocal("playState", null);
		    setLocal("dialogueState", null);
		}
		
		const callback = (receivedJSON) => {
		    if (receivedJSON.status === "codeOK")
		    {
			setLoading(true);
			let state = { currentGameId: data.gameId, currentPlayId: receivedJSON.playId };
			const content = {};

			const files = [];
			const promises = [];

			files.push({ type: "params", url: data.gameId + "/params.json" });
			files.push({ type: "chars", url: data.gameId + "/chars.json" });
			files.push({ type: "places", url: data.gameId + "/places.json" });
			files.push({ type: "themes", url: data.gameId + "/themes.json" });

			files.forEach((file) => {
			    promises.push(
				fetch(file.url)
				    .then((response) => { return response.text(); })
				    .then((text) => { content[file.type] = JSON.parse(text); })
			    );
			});

			Promise.all(promises)
			       .then(() => {

				   // PRELOAD PICTURES
				   Object.values(content.chars).forEach((char) => {
				       new Image().src = data.gameId + "/pictures/chars/" + char.id + ".png";
				       //new Image().src = data.gameId + "/pictures/chars/" + char.id + "-thumbnail.png";
				   });
				   
				   Object.values(content.places).forEach((place) => {
				       new Image().src = data.gameId + "/pictures/places/" + place.id + ".jpg";
				   });

				   const localState = action.type === "loadPlay" ? getLocal("playState") : null;
				   if (localState !== null) state = localState;

				   if (localState === null)
				   {
				       state.score = content.params.minScore;
				       
				       state.places = Object.fromEntries(
					   Object.entries(content.places).map(([id, char]) => { return [id, { id }]; })
				       );

				       state.chars = Object.fromEntries(
					   Object.entries(content.chars).map(([id, char]) => { return [id, { id, active: false }]; })
				       );

				       state.themes = Object.fromEntries(
					   Object.entries(content.themes).map(([id, theme]) => { return [id, { id, state: "todo", startRealTime: null, endRealTime: null, startVariables: null, endVariables: null, startMoney: null, endMoney: null, choiceNums: [] }]; })
				       );
				   }

				   if (! content.params.solutionGameplay && ! content.params.itemGameplay)
				   {
				       dispatch({ state, content });
				       setLoading(false);
				       if (content.params.autoFullscreen) document.body.requestFullscreen().catch((err) => {});
				       // TODO : add dialogue load if local state defined? Or more elegant: remove and treat as no promise in next block
				   }
				   
				   else {
				       const files = [];
				       const promises = [];
				       
				       let results = {};
				       if (content.params.solutionGameplay)
				       {
					   results = { ...results, solutions: {}, documents: {} };

					   files.push({ type: "variables", url: data.gameId + "/variables.json" });

					   Object.keys(state.themes).forEach((themeId) => {
					       files.push({ type: "solutions", themeId, url: data.gameId + "/themes/" + themeId + "_solutions.json" });
					       files.push({ type: "documents", themeId, url: data.gameId + "/themes/" + themeId + "_documents.json" });
					   });
				       }
				       
				       if (content.params.itemGameplay)
				       {
					   results = { ...results, items: {} };
					   
					   Object.keys(state.themes).forEach((themeId) => {
					       files.push({ type: "items", themeId, url: data.gameId + "/themes/" + themeId + "_items.json" });
					   });
				       }

				       if (localState !== null && localState.currentThemeId !== null)
				       {
					   files.push({ type: "dialogue", themeId: localState.currentThemeId, url: data.gameId + "/themes/" + localState.currentThemeId + "_dialogues.json" });
				       }

				       files.forEach((file) => {
					   promises.push(
					       fetch(file.url)
						   .then((response) => { return response.text(); })
						   .then((text) => {
						       if (file.type === "dialogue") DialogueEngine.start(JSON.parse(text).content);
						       else {
							   if (file.themeId === undefined) content[file.type] = JSON.parse(text);
							   else results[file.type][file.themeId] = JSON.parse(text);
						       }
						   })
					   );
				       });

				       Promise.all(promises)
					      .then(() => {

						  if (content.params.solutionGameplay)
						  {
						      if (localState === null)
						      {
							  state.variables = Object.fromEntries(
							      Object.entries(content.variables).map(([id, variable]) => { return [id, { id, value: variable.startValue }]; })
							  );
						      }

						      content.solutions = {};
						      Object.values(results.solutions).forEach(solutions => Object.values(solutions).forEach(solution => content.solutions[solution.id] = solution));

						      content.documents = {};
						      Object.values(results.documents).forEach(documents => Object.values(documents).forEach(document => content.documents[document.id] = document));

						      if (localState === null)
						      {
							  state.solutions = Object.fromEntries(
							      Object.entries(content.solutions).map(([id, solution]) => { return [id, { id, found: false, applied: null, time: null }]; })
							  );
							  
							  state.documents = Object.fromEntries(
							      Object.entries(content.documents).map(([id, document]) => { return [id, { id, found: false, time: null }]; })
							  );

							  state.reports = Object.fromEntries(
							      Object.entries(content.solutions).map(([id, solution]) => { return [id, {
								  id,
								  result: null,
								  collection: { documentIds: [] },
								  investment: { documentIds: [] },
								  variables: Object.fromEntries(
								      Object.entries(content.variables).map(([id, variable]) => { return [id, { id, pos: { documentIds: [] }, neg: { documentIds: [] } }]; })
								  ),
							      }]; })
							  );
						      }
						  }
						  
						  if (content.params.itemGameplay)
						  {
						      content.items = {};
						      Object.values(results.items).forEach(items => Object.values(items).forEach(item => content.items[item.id] = item));

						      if (localState === null)
						      {
							  state.items = Object.fromEntries(
							      Object.entries(content.items).map(([id, item]) => { return [id, { id, found: false, appliedToCharIds: [], missedToCharIds: [], time: null }]; })
							  );
						      }
						  }

						  dispatch({ state, content });
						  setLoading(false);
						  if (content.params.autoFullscreen) document.body.requestFullscreen().catch((err) => {});
					      });
				   }
			       });
		    }
		    
		    else if (receivedJSON.status === "codeKO") asyncDispatch({ type: "alert", data: { variant: "danger", title: "Code d'accès inconnu" }});

		    else asyncDispatch({ type: "alert", data: { variant: "danger", title: "Erreur interne" }});
		};

		if (action.type === "newPlay")
		{
		    api("newPlay.php", { name: action.name, code: action.code }, true, callback);
		}
		
		else if (action.type === "loadPlay")
		{
		    const localState = getLocal("playState");
		    callback({ playId: localState.playId, status: "codeOK" });
		}
	    }
	    break;

	    case "startTheme" :
	    fetch(data.state.currentGameId + "/themes/" + action.themeId + "_dialogues.json")
		.then((response) => { return response.text(); })
		.then((text) => {
		    const result = JSON.parse(text);
		    DialogueEngine.start(result.content);

		    const themeContent = data.content.themes[action.themeId];

		    let newState = {
			currentThemeId: action.themeId,
			currentPlaceId: themeContent.startPlaceId,
			time: themeContent.startTime || null,
			timeOut: false,
			showModals: { title: true }
		    }
		    
		    dispatch({ state: newState });	
		});
	    break;

	    case "endTheme" :
	    const theme = data.state.themes[data.state.currentThemeId];
	    dispatch({ state: {
		currentThemeId: null,
		themes: { [theme.id]: { ...theme, state: "done" } }
	    } });	
	    break;

	    case "startRealTime":
	    {
		const newTheme = { ...data.state.themes[data.state.currentThemeId] };
		newTheme.startRealTime = Date.now();
		newTheme.startVariables = { ...data.state.variables };
		newTheme.startMoney = data.state.money;
		dispatch({ state: { themes: { [newTheme.id]: newTheme } } });
	    }
	    break;
	    
	    case "endRealTime":
	    {
		const newTheme = { ...data.state.themes[data.state.currentThemeId] };
		newTheme.endRealTime = Date.now();
		newTheme.endVariables = { ...data.state.variables };
		newTheme.endMoney = data.state.money;

		const newSolutions = {};
		Object.values(data.state.solutions)
		      .filter((solution) => {
			  const solutionContent = data.content.solutions[solution.id];
			  return solutionContent.themeId === newTheme.id && solution.applied === null;
		      })
		      .forEach((solution) => { newSolutions[solution.id] = { ...solution, applied: false }; });
		
		dispatch({ state: { themes: { [newTheme.id]: newTheme }, solutions: newSolutions } });
	    }
	    break;

	    case "goToPlace" :
	    dispatch({ state: { currentPlaceId: action.placeId, showModals: { dialogue: true } } });
	    break;

	    case "goToChar" :
	    {
		DialogueEngine.goto(action.charId);
		const currentDialogue = DialogueEngine.current();
		dispatch({ state: { currentDialogue, showModals: { dialogue: true } } });
	    }
	    break;

	    case "goToPlaceAndChar" :
	    {
		DialogueEngine.goto(action.charId);
		const currentDialogue = DialogueEngine.current();
		dispatch({ state: { currentPlaceId: action.placeId, currentDialogue, showModals: { dialogue: true } } });
	    }
	    break;

	    case "nextDialogue" :
	    {
		DialogueEngine.next();
		const currentDialogue = DialogueEngine.current();
		//const newChars = {};
		//currentDialogue.chars.forEach((charId) => { if (! data.state.chars[charId].active) newChars[charId] = { id: charId, active: true }; });
		//Object.entries(data.state.chars).forEach(([id, char]) => { if (! currentDialogue.chars.includes(char.id)) newChars[char.id] = { id: char.id, active: false }; });
		dispatch({ state: { currentDialogue } }); //, chars: newChars } });
	    }
	    break;

	    case "closeDialogue" :
	    dispatch({ state: { currentDialogue: null, showModals: { dialogue: false } } });
	    break;

	    case "choiceDialogue" :
	    {
		const theme = data.state.themes[data.state.currentThemeId];
		const choiceNums = theme.choiceNums;

		DialogueEngine.goto(action.label);
		const currentDialogue = DialogueEngine.current();
		/* const newChars = {};
		   currentDialogue.chars.forEach((charId) => { if (! data.state.chars[charId].active) newChars[charId] = { id: charId, active: true }; });
		   Object.entries(data.state.chars).forEach(([id, char]) => { if (! currentDialogue.chars.includes(char.id)) newChars[char.id] = { id: char.id, active: false }; }); */
		dispatch({ state: {
		    currentDialogue,
		    time: data.state.time + action.time,
		    themes: { [data.state.currentThemeId]: { ...theme, choiceNums: [ ...choiceNums, action.num ] } },
		    //chars: newChars
		} });
	    }
	    break;

	    case "addCharQuests" :
	    {
		const newQuests = Object.fromEntries(Object.values(action.charQuests).map((quest) => {
		    const prevQuest = data.state.charQuests[quest.id];
		    const time = prevQuest === undefined ? Date.now() : prevQuest.time;
		    return [quest.id, { ...quest, time }];
		}));	    
		dispatch({ state: { charQuests: newQuests } });
	    }
	    break;

	    case "addChoiceQuests" :
	    {
		const newQuests = Object.fromEntries(Object.values(action.choiceQuests).map((quest) => {
		    const prevQuest = data.state.choiceQuests[quest.id];
		    const time = prevQuest === undefined ? Date.now() : prevQuest.time;
		    return [quest.id, { ...quest, time }];
		}));	    
		dispatch({ state: { choiceQuests: newQuests } });
	    }
	    break;

	    case "addBadges" :
	    {
		Object.values(action.badges).forEach((badge) => { DialogueEngine.addBadge(badge.id); });
		const newBadges = Object.fromEntries(Object.values(action.badges).map((badge) => { return [badge.id, { ...badge, time: Date.now() }]; }));	    
		dispatch({ save: true, state: { badges: newBadges } });
	    }
	    break;
	    

	    /* case "removeQuests" :
	       {
	       let newQuests = {};
	       action.charIds.forEach((charId) => newQuests[charId] = { charId, active: false });
	       dispatch({ state: { quests: newQuests } });
	       }
	       break; */

	    case "addSolutions" :
	    {
		let newSolutions = {};
		let newBadges = {};

		action.solutionIds.forEach((solutionId) => {
		    newSolutions[solutionId] = { ...data.state.solutions[solutionId], found: true, time: Date.now() };

		    DialogueEngine.addBadge(solutionId);
		    newBadges[solutionId] = { id: solutionId, state: "opened", time: Date.now() };
		});
		
		dispatch({ state: { solutions: newSolutions, badges: newBadges } });
	    }
	    break;
	    
	    case "addDocuments" :
	    {
		let newDocuments = {};
		let newReports = {};
		let newBadges = {};

		action.documentIds.forEach((documentId) => {
		    const documentContent = data.content.documents[documentId];
		    if (data.state.currentGameId !== "ada" && documentContent.type === "info") return;
		    newDocuments[documentId] = { ...data.state.documents[documentId], found: true, time: Date.now() };

		    DialogueEngine.addBadge(documentId);
		    newBadges[documentId] = { id: documentId, state: "opened", time: Date.now() };

		    const solutionId = documentContent.solutionId;
		    let newCollectionDocumentIds = [];
		    if (newReports[solutionId] === undefined) newCollectionDocumentIds = [ ...data.state.reports[solutionId].collection.documentIds, documentId ];
		    else newCollectionDocumentIds = [ ...newReports[solutionId].collection.documentIds, documentId ];
		    newReports[solutionId] = { ...data.state.reports[solutionId], collection: { documentIds: newCollectionDocumentIds } };
		});

		dispatch({ state: { documents: newDocuments, reports: newReports, badges: newBadges } });
	    }
	    break;

	    case "addItems" :
	    {
		let newItems = {};
		let newBadges = {};

		action.itemIds.forEach((itemId) => {
		    newItems[itemId] = { ...data.state.items[itemId], found: true, time: Date.now() };

		    DialogueEngine.addBadge(itemId);
		    newBadges[itemId] = { id: itemId, state: "opened", time: Date.now() };
		});

		dispatch({ save: true, state: { items: newItems, badges: newBadges } });
		
		if (Object.values(data.content.items).filter((item) => item.type === "indice").every((item) => action.itemIds.includes(item.id) || data.state.items[item.id].found))
		    asyncDispatch({ type: "alert", data: { variant: "success", title: "Enquête presque résolue !", content: "Bravo, vous avez récupéré trois indices vous permettant d'identifier qui est derrière toute cette affaire. Il est temps de confronter une dernière fois le ou la coupable !" }})
	    }
	    break;

	    case "leavePlace" :
	    dispatch({ state: { currentPlaceId: null } });
	    break;

	    case "addToScore" :
	    {
		let score = data.state.score + action.value;
		if (score < 0) score = 0; else if (score > 100) score = 100;
		dispatch({ save: true, state: { score } });
	    }
	    break;

	    case "addToErrors" :
	    {
		let errors = data.state.errors + action.value;
		if (errors < 0) errors = 0;
		dispatch({ save: true, state: { errors } });
	    }
	    break;

	    case "addReportResult" :
	    dispatch({ state: { reports: { [action.solutionId]: { ...data.state.reports[action.solutionId], result: action.result }  } } });		
	    break;

	    case "applyItems" :
	    {
		const newItems = Object.fromEntries(action.itemIds.map((itemId) => {
		    const item = { ...data.state.items[itemId] };
		    item.appliedToCharIds.push(action.charId);
		    return [item.id, item];
		}));
		
		dispatch({ state: { items: newItems } });
	    }
	    break;

	    case "missItems" :
	    {
		const newItems = Object.fromEntries(action.itemIds.map((itemId) => {
		    const item = { ...data.state.items[itemId] };
		    item.missedToCharIds.push(action.charId);
		    return [item.id, item];
		}));
		
		dispatch({ state: { items: newItems } });
	    }
	    break;

	    case "applySolution" :
	    {
		const solutionContent = data.content.solutions[action.solutionId];

		const newVariables = Object.fromEntries(
		    Object.entries(data.state.variables).map(([id, variable]) => {
			const newVariable = { ...variable };
			newVariable.value += solutionContent.variables[variable.id].impact;
			return [variable.id, newVariable];
		    })
		);

		const newMoney = data.state.money - solutionContent.cost;

		const newSolutions = {};
		newSolutions[action.solutionId] = { ...data.state.solutions[action.solutionId], applied: true };

		const group = data.content.solutions[action.solutionId].group;
		if (group !== null)
		{
		    Object.values(data.content.solutions)
			  .filter((solutionContent) => solutionContent.themeId === data.content.solutions[action.solutionId].themeId && solutionContent.id !== action.solutionId && solutionContent.group === group && data.state.solutions[solutionContent.id].applied === null)
			  .forEach((solutionContent) => {
			      newSolutions[solutionContent.id] = { ...data.state.solutions[solutionContent.id], applied: false };
			  });
		}
		
		dispatch({ state: { variables: newVariables, money: newMoney, solutions: newSolutions } });
	    }
	    break;

	    case "rejectSolution" :
	    {
		const newSolution = { ...data.state.solutions[action.solutionId], applied: false };
		dispatch({ state: { solutions: { [action.solutionId]: newSolution } } });
	    }
	    break;

	    case "moveDocument" :
	    {
		const currentReport = data.state.reports[action.solutionId];
		const newCollection = currentReport.collection;
		const newInvestment = currentReport.investment;
		const newVariables = currentReport.variables;

		action.moves.forEach((move) => {
		    if (move.from.variableId === "collection")
		    {
			const fromDocumentIds = [ ...newCollection.documentIds ];
			fromDocumentIds.splice(move.from.index, 1);
			newCollection.documentIds = fromDocumentIds;
		    }
		    
		    else if (move.from.variableId === "investment")
		    {
			const fromDocumentIds = [ ...newInvestment.documentIds ];
			fromDocumentIds.splice(move.from.index, 1);
			newInvestment.documentIds = fromDocumentIds;
		    }
		    
		    else {
			const fromDocumentIds = [ ...newVariables[move.from.variableId][move.from.value].documentIds ];
			fromDocumentIds.splice(move.from.index, 1);
			newVariables[move.from.variableId][move.from.value].documentIds = fromDocumentIds;
		    }

		    if (move.to.variableId === "collection")
		    {
			const toDocumentIds = [ ...newCollection.documentIds ];
			toDocumentIds.splice(move.to.index, 0, move.documentId);
			newCollection.documentIds = toDocumentIds;
		    }
		    
		    else if (move.to.variableId === "investment")
		    {
			const toDocumentIds = [ ...newInvestment.documentIds ];
			toDocumentIds.splice(move.to.index, 0, move.documentId);
			newInvestment.documentIds = toDocumentIds;
		    }
		    
		    else {
			const toDocumentIds = [ ...newVariables[move.to.variableId][move.to.value].documentIds ];
			toDocumentIds.splice(move.to.index, 0, move.documentId);

			if (toDocumentIds.length > 2)
			{
			    const toDocumentIds2 = [ ...newCollection.documentIds ];
			    toDocumentIds2.push(toDocumentIds[toDocumentIds.length-1]);
			    newCollection.documentIds = toDocumentIds2;
			    toDocumentIds.splice(-1);
			}
			
			newVariables[move.to.variableId][move.to.value].documentIds = toDocumentIds;

		    }
		});

		dispatch({ state: { reports: { [action.solutionId]: { ...currentReport, collection: newCollection, investment: newInvestment, variables: newVariables } } } });
	    }
	    break;
	    
	    default: console.error("unknown dispatch action: " + action.type);
	}
    }, [data]);

    useEffect(() => {
	//console.log({...data});
	if (VERBOSE)
	{
	    console.log("NEW DATA");
	    console.log(JSON.parse(JSON.stringify(data)));
	}
    }, [data]);

    /* useEffect(() => {
       if (data.session.currentSessionId !== null)
       {
       let datetime = null;
       clearInterval(refreshInterval);
       refreshInterval = setInterval(() => {
       asyncDispatch({ type: "updateSession", datetime, callback: (receivedJSON) => { datetime = receivedJSON.datetime; } });
       }, REFRESH_TIME);
       }
       else clearInterval(refreshInterval); // TODO: remove data
     * }, [data.session.currentSessionId, asyncDispatch]);
     */

    async function api (scriptFile, sentData, wait = true, callback = () => {})
    {
	if (wait) setLoading(true);
	const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(sentData),
	};

	let response;
	let receivedText;
	let receivedJSON;
	try {
	    response = await fetch(PHP_PATH + scriptFile, requestOptions);
	    receivedText = await response.text();
	    receivedJSON = JSON.parse(receivedText);
	    //if (JSON.stringify(Object.keys(receivedJSON)) !== JSON.stringify(["success", "datetime"])) dispatch({ data: receivedJSON }); // TODO : check recomputation if specific data changes
	    if (wait) setLoading(false);
	    setConnectionLost(null);
	    setInternalError(null);
	    callback(receivedJSON);
	}

	catch (error)
	{
	    if (response.status === 200) setInternalError(receivedText);
	    else setConnectionLost(true);
	    
	    if (wait) setLoading(false);
	}
    }

    return (
	<DataContext.Provider value={data}>
	    <DispatchContext.Provider value={asyncDispatch}>
		<ConnectionLost show={connectionLost !== null}/>
		<InternalError show={internalError !== null} error={internalError}/>
		<AlertList/>
		{loading && <Wait/>}
		{children}
	    </DispatchContext.Provider>
	</DataContext.Provider>
    );
    // Note: memoize the context value, if Provider gets re-rendered more often
}

export function useData ()
{
    return useContext(DataContext);
}

export function useDispatch ()
{
    return useContext(DispatchContext);
}

